/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @pandacss/no-hardcoded-color */
"use client";

import * as ToastPrimitives from "@radix-ui/react-toast";
import * as React from "react";
import { LegacyRef } from "react";

import {
  type RecipeVariantProps,
  css,
  cva,
  cx,
  sva,
} from "@/../styled-system/css";
import Cross from "@/assets/svg/x.svg";

const toast = sva({
  slots: ["viewport", "action", "close", "title", "description"],
  base: {
    viewport: {
      position: "fixed",
      top: "0",
      zIndex: "100",
      display: "flex",
      maxH: "screen",
      w: "fit",
      flexDir: "column-reverse",
      p: "4",
      md: {
        bottom: "0",
        right: "0",
        top: "auto",
        flexDir: "column",
        maxW: "600px",
      },
    },
    action: {
      display: "inline-flex",
      flexShrink: "0",
      alignItems: "center",
      justifyContent: "center",
      py: "1.5",
      px: "4",
      mx: "auto",
      fontSize: "md",
      fontWeight: "semibold",
      lineHeight: "tight",
      transition: "colors",
      color: "brandBlue",
      cursor: "pointer",
      borderWidth: "2px",
      borderColor: "transparent",
      rounded: "lg",
      _disabled: { pointerEvents: "none", opacity: "0.5" },
      _hover: {
        borderColor: "brandBlue",
      },
      ".group:is(.destructive) &": {
        color: "red.600",
        _hover: {
          borderColor: "red.600",
        },
      },
      ".group:is(.warning) &": {
        color: "yellow.300",
        _hover: {
          borderColor: "yellow.300",
        },
      },
    },
    close: {
      position: "absolute",
      right: "1",
      top: "1",
      rounded: "md",
      p: "1",
      opacity: "0",
      transitionProperty: "opacity",
      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      transitionDuration: "150ms",
      cursor: "pointer",
      _focus: {
        opacity: "1",
      },
      _groupHover: { opacity: "1" },
    },
    title: {
      fontSize: "md",
      lineHeight: "tight",
      fontWeight: "semibold",
      color: "white",
    },
    description: {
      fontSize: "sm",
      lineHeight: "tight",
      opacity: "0.9",
      color: "stone.300",
    },
  },
});

const { viewport, action, close, title, description } = toast.raw();

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & {
    css?: object;
  }
>(
  (
    { css: cssProp = {}, ...props }: any,
    ref: LegacyRef<HTMLOListElement> | undefined,
  ) => {
    const className = css(viewport, cssProp);

    return (
      <ToastPrimitives.Viewport ref={ref} className={className} {...props} />
    );
  },
);
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva({
  base: {
    pointerEvents: "auto",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "2.5",
    w: "full",
    alignItems: "center",
    overflow: "hidden",
    rounded: "md",
    py: "4",
    px: "6",
    shadow: "lg",
    translate: "auto",
    backgroundColor: "brandBlueCharcoal",
    transition: "all",
    "&[data-swipe=cancel]": {
      transform: "translateY(0)",
    },
    "&[data-swipe=end]": {
      transform: "translateY(var(--radix-toast-swipe-end-y))",
    },
    "&[data-swipe=move]": {
      transform: "translateY(var(--radix-toast-swipe-move-y))",
      transition: "none",
    },
    "&[data-state=open]": {
      animateIn: true,
      slideInFromTop: "calc((100% + 25px))",
    },
    "&[data-state=closed]": {
      animateOut: true,
      fadeOut: "0.8",
      slideOutToTop: "calc((100% + 25px))",
    },
    md: {
      flexDirection: "row",
      "&[data-swipe=cancel]": {
        transform: "translateX(0)",
      },
      "&[data-state=open]": {
        slideInFromBottom: "calc(100% + 20px)",
      },
      "&[data-state=closed]": {
        slideOutToTop: "0%",
        slideOutToRight: "calc(100% + 20px)",
      },
      "&[data-swipe=end]": {
        transform: "translateX(var(--radix-toast-swipe-end-x))",
      },
      "&[data-swipe=move]": {
        transform: "translateX(var(--radix-toast-swipe-move-x))",
      },
    },
  },
  variants: {
    variant: {
      default: {
        background:
          "linear-gradient(to right, rgba(65, 200, 230, .2) 0%, transparent 140px )",
      },
      warning: {
        background:
          "linear-gradient(to right, rgba(255, 212, 38, .2) 0%, transparent 140px )",
      },
      destructive: {
        background:
          "linear-gradient(to right, rgba(240, 66, 72, .2) 0%, transparent 140px )",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    RecipeVariantProps<typeof toastVariants> & { css?: object }
>(
  (
    {
      variant = "default",
      css: cssProp = {},
      ...props
    }: { variant?: "default" | "warning" | "destructive"; css?: object },
    ref: LegacyRef<HTMLLIElement> | undefined,
  ) => {
    const className = cx(
      css(toastVariants.raw({ variant }), cssProp),
      variant != "default" ? `group ${variant}` : "",
    );

    return <ToastPrimitives.Root ref={ref} className={className} {...props} />;
  },
);
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> & {
    css?: object;
  }
>(
  (
    {
      css: cssProp = {},
      altText = "",
      ...props
    }: { css?: object; altText: string },
    ref: LegacyRef<HTMLButtonElement> | undefined,
  ) => {
    const className = css(action, cssProp);

    return (
      <ToastPrimitives.Action
        ref={ref}
        className={className}
        altText={altText}
        {...props}
      />
    );
  },
);
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close> & {
    css?: object;
  }
>(
  (
    { css: cssProp = {}, ...props }: any,
    ref: LegacyRef<HTMLButtonElement> | undefined,
  ) => {
    const className = css(close, cssProp);

    return (
      <ToastPrimitives.Close ref={ref} className={className} {...props}>
        <Cross stroke="white" width="1rem" />
      </ToastPrimitives.Close>
    );
  },
);
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title> & {
    css?: object;
  }
>(
  (
    { css: cssProp = {}, ...props }: any,
    ref: LegacyRef<HTMLDivElement> | undefined,
  ) => {
    const className = css(title, cssProp);

    return <ToastPrimitives.Title ref={ref} className={className} {...props} />;
  },
);
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description> & {
    css?: object;
  }
>(
  (
    { css: cssProp = {}, ...props }: any,
    ref: LegacyRef<HTMLDivElement> | undefined,
  ) => {
    const className = css(description, cssProp);

    return (
      <ToastPrimitives.Description ref={ref} className={className} {...props} />
    );
  },
);
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastActionElement,
  type ToastProps,
};
